/* eslint-disable no-param-reassign */
import moment from 'moment';
import { featureEnabled, correctShowingTypeLabel } from 'utils';
import _ from 'lodash';
import axios from 'axios';
import type {
  AppointmentType,
  EnhancedAppointment,
  JsonObject,
  ParsedAppointment,
} from '@bb/domain/dist/types';
import type Appointment from '@bb/domain/dist/types/Appointment';
import type { CombinedListingDTO } from '@bb/domain/dist/types/dto/AppointmentController/findOrgDTO';
import { Feature } from '@bb/automaton';
import type { AuthContextType, ListingLabelContextType } from 'contexts';
import { parseLabelFromString } from 'contexts';
import { theme } from 'components';
import type { FEATURE_FLAGS } from 'utils/featureFlagBrowserClient';
import type { ListingDoc } from '@bb/domain/dist/schemas';
import ngAnnotate from '../ngAnnotate.macro';
import pageTemplate from './admin_dashboard.html';
import { APPOINTMENT_STATUS_FOR_COVID_19_LABEL } from '../constants';
import type { AdminService } from './admin.service';

class AdminDashboardCtrl {
  auth: AuthContextType['auth'];
  appointmentsViewed: Record<string, boolean>;
  appointmentsWhileLoading: todo[];

  counts: { unassigned?: number; queue?: number; exclusives?: number };

  datacount: number;
  dataLoaded: boolean;
  data: { appointment: todo; listing: todo }[];

  search: {
    text?: string;
    location: string;
    locationIndices?: string;
    status: string;
    statuses?: string;
    requestType: string;
    showDuplicateHistory?: boolean;
    showUncombined?: boolean;
    page?: number;
    queueLocations?: todo;
    queueStatuses?: todo;
  };

  sort: { reverse: boolean; type: string };

  group: Record<string, any>;
  queueLocations: todo[];
  savingPreferences: boolean;
  selectedAppointments: Record<string, any>;
  showDuplicateHistory: boolean;
  showLAToConfirm: boolean;
  showUnassigned: boolean;
  showUncombined: boolean;
  vm: any;
  preferences: {
    locations: Record<string, boolean>;
    statuses: Record<string, boolean>;
  };

  APPOINTMENT_STATUS_FOR_COVID_19_LABEL: string[];
  branchFilterableTabs: string[];
  count: number;
  deletedLocations: any[];
  displayStatuses: { label: string; status: string }[];
  filterableTabs: Set<string>;
  locationNames: string[];
  MAX_SERVICE_APPTS: number;
  pagination: { maxSize: number; itemsPerPage: number; total?: number };
  preferencesSet: boolean;
  selectAllPreferencesStatuses: any;
  sortStates: Record<string, todo>;
  statuses: string[];
  statusFilterableTabs: string[];
  hasFeature: AuthContextType['hasFeature'];
  getLabel: (v: string) => string;

  getMaxPageNumber: (total: number) => number;
  changePage: (page: number) => void;
  clearPreferences: () => PromiseLike<void>;
  dismiss: (duplicate: { id: string }) => void;
  dismissNoMoreShowings: (d: todo) => void;
  flagAppointment: (dataDetails: todo) => void;
  getFilteredAppointmentsCount: () => number;
  getGroupCount: (groupName: 'coop' | 'address', d: todo) => number;
  getImages: (listing: todo) => void;
  getMultiConfStatus: (appointment: todo) => string;
  getSelectedAppointments: () => string[];
  getUnconfiguredMissing: (obj: todo) => string;
  load: () => void;
  loadPreferences: () => PromiseLike<void>;
  numHiddenLAToConfirm: () => number;
  onQueueSearchChange: () => void;
  setExclusivesCount: (exCount: number) => void;
  refreshStatuses: () => void;
  savePreferences: () => void;
  viewListing: (id: string) => void;
  selectAll: () => void;
  showFilter: () => boolean;
  selectAllPreferencesLocations: () => void;
  selectTab: (requestType: string) => void;
  serviceAppointments: () => void;
  setGroup: (groupName: string, d: todo) => void;
  setNoMoreShowings: (d: todo) => void;
  setSort: (name: string) => void;
  setUrgency: (dataDetails: todo) => void;
  shouldLoadImage: (slides: todo[], index: number) => boolean;
  showPreferencesModal: () => void;
  submitSearch: () => void;
  toggleDuplicateHistory: () => void;
  toggleLAConfirm: () => void;
  toggleShowUncombined: () => void;
  uncombine: (combinedListingDoc: CombinedListingDTO[number]) => void;
  isAuthorizedToAccessLocation: (index: number) => boolean;
  getStatusText: (appt: EnhancedAppointment) => string;
  filterAppointments: (appointmentData: {
    listing: JsonObject<ListingDoc>;
    appointment: ParsedAppointment;
  }) => boolean;

  deselectAllPreferencesLocations: () => void;
  deselectAllPreferencesStatuses: () => void;
  handleSort: (tab: string, sortOptions: todo) => void;
  offerListing: any;
  openContactDrawer: () => void;
  viewDashAppointment: (appointment: todo, arg: { sids: string[] }) => void;

  locationIsNotDeleted: (queueLocationValue: string) => boolean;
  setOfferListing: (listing: todo) => void;

  relabelListingAvailability: (availability: string) => string;

  getBoardName: (board: string, useOverride?: boolean) => string;

  featureEnabled: (flag: keyof typeof FEATURE_FLAGS) => boolean;
  correctShowingTypeLabel: (type: AppointmentType) => string;

  constructor(
    private $scope: ng.IScope,
    private $resource: ng.resource.IResourceServiceProvider,
    private $location: ng.ILocationService,
    private $rootScope: ng.IRootScopeService & {
      auth: todo;
      hasFeature: AuthContextType['hasFeature'];
      openContactDrawer(): void;
      viewDashAppointment(appointment: todo, arg: { sids: string[] }): void;
    } & Pick<ListingLabelContextType, 'labelOverrideMap' | 'getBoardName'>,
    private $routeParams: ng.route.IRouteParamsService,
    private $interval: ng.IIntervalService,
    private $filter: ng.IFilterService,
    private NoMoreShowingsService: todo,
    // eslint-disable-next-line @typescript-eslint/no-shadow
    AdminService: AdminService,
  ) {
    // #region Injectables

    this.auth = $rootScope.auth;
    this.hasFeature = $rootScope.hasFeature;

    this.getLabel = v =>
      this.$rootScope.labelOverrideMap[parseLabelFromString(v)]?.label || v;

    // #endregion Injectables

    // #region Event Handlers
    if (
      this.auth.roles[this.auth.role] === this.auth.roles.TEAM_ADMIN &&
      !this.hasFeature(Feature.TEAMS)
    ) {
      $location.path('/feature-missing');
    }

    this.$scope.$on('viewing_start', (events, entry) => {
      if (this.search.requestType !== 'queue') {
        return;
      }

      this.appointmentsViewed[entry.appointmentId] = true;
      this.$scope.$digest();
    });

    this.$scope.$on('viewing_stop', (events, entry) => {
      if (this.search.requestType !== 'queue') {
        return;
      }

      this.appointmentsViewed[entry.appointmentId] = false;
      this.$scope.$digest();
    });

    this.$scope.$on('appointment_status', (events, entry) => {
      // Reload count
      if (this.search.requestType !== 'queue') {
        (async () => {
          const locations = this.search.locationIndices
            ?.split(',')
            .filter(Boolean);
          const { data } = await axios.get('/appointments/badge_count', {
            params: { locations },
          });
          this.counts.queue = data.queueCount;
          this.$scope.$digest();
        })();
        return;
      }

      const updateQueueAppointment = (updatedAppointment: todo) => {
        const index = this.data.findIndex(
          d => d.appointment.id === updatedAppointment.id,
        );

        let removedRow: (typeof this.data)[number] | null = null;
        if (index > -1) [removedRow] = this.data.splice(index, 1);

        if (updatedAppointment.isPending && !updatedAppointment.isDeleted) {
          const updatedRow = {
            listing: updatedAppointment.listing,
            appointment: updatedAppointment,
          };

          this.setUrgency(updatedRow);
          this.data.push(updatedRow as any);
        }

        this.count = this.data.length;
        this.counts.queue = this.getFilteredAppointmentsCount();
      };

      updateQueueAppointment(entry.appointment);
    });

    this.$scope.$on('appointment_flagged', (events, entry) => {
      if (this.search.requestType !== 'queue') {
        return;
      }

      _.each(this.data, dataDetails => {
        if (dataDetails.appointment.id === entry.appointmentId) {
          dataDetails.appointment.flagged = entry.flagged;
          this.setUrgency(dataDetails);
          this.$scope.$digest();
        }
      });
    });

    this.$scope.$on('appointment_covid19_signed', (events, entry) => {
      const updateCovidSignStatus = () => {
        const { appointmentId } = entry;
        const appointmentIndex = this.data.findIndex(
          ({ appointment }) => appointment.id === appointmentId,
        );
        if (this.data[appointmentIndex]?.appointment.covid19RequestedAck) {
          this.data[appointmentIndex].appointment.covid19Signed = true;
        }
      };
      if (this.dataLoaded && this.search.requestType === 'queue')
        this.$scope.$evalAsync(updateCovidSignStatus);
    });

    this.$scope.$on('$destroy', () => {
      if (countPromise) this.$interval.cancel(countPromise);
      if (refreshStatusesPromise) this.$interval.cancel(refreshStatusesPromise);
    });

    // #endregion Event Handlers

    // #region Public Methods

    // Shows appointment type / location filter
    this.showFilter = () =>
      this.auth.roles[this.auth.role] >= this.auth.roles.GLOBAL_ADMIN ||
      (this.auth.roles[this.auth.role] === this.auth.roles.BRANCH_ADMIN &&
        this.auth?.locations?.length > 1) ||
      this.search.requestType === 'queue';

    this.getSelectedAppointments = () => {
      const appointments: string[] = [];
      _.each(this.selectedAppointments, (selected, appointmentId) => {
        if (selected) appointments.push(appointmentId);
      });
      return appointments;
    };

    this.selectAll = () => {
      this.data.filter(this.filterAppointments).forEach(d => {
        this.selectedAppointments[d.appointment.id] = this.vm.allAppointments;
      });
    };

    this.getFilteredAppointmentsCount = () =>
      this.data.filter(this.filterAppointments).length;

    this.serviceAppointments = () => {
      const ordered: string[] = [];
      const selected = this.getSelectedAppointments();

      let firstAppointment;

      let index = 0;
      _.each(
        this.$filter('orderBy')(this.data, [
          'appointment.urgency',
          this.sort.type,
        ]),
        d => {
          if (selected.indexOf(d.appointment.id) !== -1) {
            if (ordered.length === 0) {
              firstAppointment = d.appointment;
            }
            ordered[index] = d.appointment.id;
            index += 1;
          }
        },
      );

      this.$rootScope.viewDashAppointment(firstAppointment, { sids: ordered });
    };

    this.changePage = page => {
      this.search.page = page;
      load();
    };

    this.submitSearch = () => {
      this.search.page = 1;
      load();
    };

    this.setSort = (name: string) => {
      this.sort.reverse = !this.sort.reverse;

      if (this.sort.reverse) this.sort.type = `-${name}`;
      else this.sort.type = name;
    };

    this.correctShowingTypeLabel = correctShowingTypeLabel;

    this.setUrgency = dataDetails => {
      const currentMoment = moment();

      let notice = 0;
      if (dataDetails.appointment.notice) {
        ({
          appointment: { notice },
        } = dataDetails);
      }
      if (dataDetails.appointment.status === 'time_passed') {
        dataDetails.appointment.urgency = 0;
      } else if (dataDetails.appointment.flagged) {
        dataDetails.appointment.urgency = 1;
      } else if (
        moment(dataDetails.appointment.time).diff(currentMoment) <
        (3 + notice) * 60 * 60 * 1000
      ) {
        dataDetails.appointment.urgency = 1;
      } else if (
        dataDetails.appointment.lastActionAt &&
        currentMoment.diff(
          moment(dataDetails.appointment.lastActionAt),
          'hours',
        ) >= 48
      ) {
        dataDetails.appointment.urgency = 3;
      } else if (dataDetails.appointment.bookedThroughMls) {
        dataDetails.appointment.urgency = 4;
      } else {
        dataDetails.appointment.urgency = 10;
      }
    };

    this.refreshStatuses = () => {
      if (this.search.requestType !== 'queue') {
        return;
      }

      _.each(this.data, dataDetails => {
        this.setUrgency(dataDetails);
      });
    };

    this.selectTab = (requestType: string) => {
      if (this.search.requestType === requestType) return;
      this.$location.path('admin/dashboard').search({ requestType });
      this.$location.replace();
      this.search.requestType = requestType;
      this.search.page = 1;
      if (requestType === 'exclusives') {
        this.dataLoaded = true;
        return;
      }
      load();
    };

    this.setExclusivesCount = (exCount: number) => {
      this.$rootScope.$evalAsync(() => {
        this.counts.exclusives = exCount;
      });
    };

    this.flagAppointment = (dataDetails: todo) => {
      dataDetails.appointment.flagged = !dataDetails.appointment.flagged;
      const {
        appointment: { id, flagged },
      } = dataDetails;
      AdminService.flagAppointment(id, flagged);
    };

    this.getMultiConfStatus = (appointment: Pick<Appointment, 'multiConf'>) => {
      const remaining = appointment.multiConf.filter(
        contact => contact.status !== 'confirm',
      );
      return `${remaining.length} of ${appointment.multiConf.length} Remaining`;
    };

    this.toggleDuplicateHistory = () => {
      this.showDuplicateHistory = !this.showDuplicateHistory;
      this.search.showDuplicateHistory = this.showDuplicateHistory;
      this.search.page = 1;
      load();
    };

    this.toggleShowUncombined = () => {
      this.search.showUncombined = this.showUncombined;
      load();
    };

    this.getMaxPageNumber = total => Math.min(total, 10000);

    this.uncombine = async (
      combinedListing: CombinedListingDTO[number] & {
        mlsNumbersAdded: JsonObject<ListingDoc>['mls_numbers'][number];
      },
    ) => {
      const {
        id,
        mlsNumbersAdded,
        to: { availability, mls_numbers: toListingMlsNumbers },
      } = combinedListing;

      /** Listings should be uncombined in the order they were combined */
      const { isLatest, combinedWithId } =
        await AdminService.isCombinedListingLatest(id);

      const isListingCombined = availability === 'Combined listing';

      if (isListingCombined || !isLatest) {
        AdminService.getListing(combinedWithId).then(
          (foundListing: JsonObject<ListingDoc>) => {
            const { line1, line2, mls_numbers } = foundListing;
            const isSameMlsNumber = (
              mls1: JsonObject<ListingDoc>['mls_numbers'][number],
              mls2: JsonObject<ListingDoc>['mls_numbers'][number],
            ) =>
              mls1.boardId.toString() === mls2.boardId.toString() &&
              mls1.number.replace(/:multi-board/g, '') ===
                mls2.number.replace(/:multi-board/g, '');

            const originalMlsNumbers = _.differenceWith(
              toListingMlsNumbers,
              mlsNumbersAdded.map(mlsNumber => ({
                ...mlsNumber,
                boardId: mlsNumber.boardId.toString(),
              })),
              isSameMlsNumber,
            );
            const formattedMlsNumbers = originalMlsNumbers
              .map(
                ({ board, number }) => `${this.getBoardName(board)}: ${number}`,
              )
              .join(', ');
            swal({
              title: 'Cannot Uncombine',
              text: `It looks like this listing has been combined with MLS:  ${formattedMlsNumbers}, ${line1}${
                line2 ? ` ${line2}` : ''
              }. Please uncombine that listing first.`,
              type: 'warning' as any,
              html: true,
              closeOnConfirm: true,
              confirmLoadingButtonColor: '#DD6B55',
              confirmButtonText: 'Ok',
            } as SweetAlert.Settings & SweetAlert.PromtModalSettings);
          },
        );
        return;
      }
      swal(
        {
          title: 'Un-combine Listings?',
          text: /* html */ `
            <div style="text-align: justify">
              <div style="margin-bottom: 10px"><span><strong>WARNING:</strong> By clicking Uncombine, this listing will be split back into 2 individual listings.</span></div>
              <p>Each listing's configuration settings will be reverted back to the original configurations that existed BEFORE the 2 listings were combined together.</p>
            </div>
          `,
          type: 'warning' as any,
          html: true,
          showCancelButton: true,
          closeOnConfirm: false,
          disableButtonsOnConfirm: true,
          confirmLoadingButtonColor: '#DD6B55',
          confirmButtonText: 'UNCOMBINE',
          confirmButtonColor: theme.blue6,
        } as SweetAlert.Settings & SweetAlert.PromtModalSettings,
        () => {
          swal({
            title: 'Uncombining Listings...',
            text: 'This will take ~5 seconds',
            type: 'warning',
            showCancelButton: false,
            showConfirmButton: false,
          });

          AdminService.uncombineListing(id)
            .then(() => {
              swal('Success!', 'Your listing has been un-combined', 'success');
              load();
            }, 1500 as todo)
            .catch(err => {
              const errMsg = err.data?.message || '';
              swal(
                'Unsuccessful',
                `There was an issue uncombining your listing. ${errMsg}`,
                'error',
              );
            });
        },
      );
    };

    this.viewListing = (id: string) => {
      if (this.search.requestType === 'unconfigured') {
        this.$location.path(`listing/${id}/appointments/config`);
      } else if (this.search.requestType === 'unassigned') {
        this.$location.path(`listing/${id}/edit`);
      } else {
        this.$location.path(`listing/${id}/view`);
      }
    };

    this.toggleLAConfirm = () => {
      this.showLAToConfirm = !this.showLAToConfirm;
    };

    this.numHiddenLAToConfirm = () => {
      const clonedData = _.cloneDeep(this.data);

      return (
        clonedData
          .filter(aptData => {
            const { appointment } = aptData;
            return (
              appointment.listingAgentConfirm &&
              appointment.listingAgentConfirm.excludeFromQueue
            );
          })
          // remove LA to confirm and see which of the appointments would be in the queue
          .map(d => {
            d.appointment.listingAgentConfirm = null;
            return d;
          })
          .filter(x => this.filterAppointments(x)).length
      );
    };

    this.filterAppointments = (appointmentData: {
      listing: JsonObject<ListingDoc>;
      appointment: ParsedAppointment;
    }) => {
      const { search, group, showLAToConfirm } = this;

      if (
        !showLAToConfirm &&
        appointmentData.appointment.listingAgentConfirm &&
        appointmentData.appointment.listingAgentConfirm.excludeFromQueue
      ) {
        return false;
      }

      return (
        search &&
        search.queueLocations &&
        [
          appointmentData.listing.location,
          // @ts-ignore
          ...(appointmentData.listing.locations || []),
        ].some(l => search.queueLocations?.[l]) &&
        search &&
        search.queueStatuses &&
        search.queueStatuses[appointmentData.appointment.status] &&
        (!search.text ||
          appointmentData.listing.line1
            .toLowerCase()
            .includes(search.text.toLowerCase()) ||
          (appointmentData.listing.line2 &&
            appointmentData.listing.line2
              .toLowerCase()
              .includes(search.text.toLowerCase())) ||
          (appointmentData.appointment.coop.name &&
            appointmentData.appointment.coop.name
              .toLowerCase()
              .includes(search.text.toLowerCase()))) &&
        coopEquals(group.coop, appointmentData) &&
        addressEquals(group.address, appointmentData)
      );
    };

    this.getGroupCount = (groupName: 'coop' | 'address', d: todo) => {
      let compareFunction: typeof coopEquals;
      if (groupName === 'coop') compareFunction = coopEquals;
      else if (groupName === 'address') compareFunction = addressEquals;

      return _.sumBy(this.data, x => (compareFunction(d, x) ? 1 : 0));
    };

    this.setGroup = (groupName: string, d: todo) => {
      this.group = {};
      this.group[groupName] = d;
    };

    this.getUnconfiguredMissing = (obj: todo) =>
      // Add more fields to the array when required in the future
      ['Access Instructions'].filter(e => !obj || obj[e] === false).join(', ');

    this.getImages = (listing: todo) => {
      listing.slides = [];

      // File Search
      AdminService.getListingImages(listing.id).then(data => {
        data.forEach((image: todo, index) => {
          listing.slides.push({ active: index === 0, url: image.url });
        });
      });
    };

    this.shouldLoadImage = (slides: todo[], index: number) =>
      [index - 1, index, index + 1].some(i => slides[i] && slides[i].active);

    this.dismiss = (duplicate: { id: string }) => {
      AdminService.dismissDuplicates(duplicate.id).then(() => {
        load();
      });
    };

    this.dismissNoMoreShowings = (d: todo) => {
      AdminService.dismissNoMoreShowings(d.listing.id).then(() => {
        removeNoMoreShowingEntry(d);
      });
    };

    this.setNoMoreShowings = (d: todo) => {
      this.NoMoreShowingsService.showNoMoreShowingsWarning(
        true,
        d.listing.id,
        (noMoreShowings: todo) => {
          if (noMoreShowings) removeNoMoreShowingEntry(d);
        },
      );
    };

    this.selectAllPreferencesLocations = () => {
      this.queueLocations.forEach(location => {
        this.preferences.locations[location] = true;
      });
    };

    this.deselectAllPreferencesLocations = () => {
      this.queueLocations.forEach(location => {
        this.preferences.locations[location] = false;
      });
    };

    this.selectAllPreferencesStatuses = () => {
      this.displayStatuses.forEach(status => {
        this.preferences.statuses[status.status] = true;
      });
    };

    this.deselectAllPreferencesStatuses = () => {
      this.displayStatuses.forEach(status => {
        this.preferences.statuses[status.status] = false;
      });
    };

    this.showPreferencesModal = () => {
      AdminService.getQueuePreferences().then((preferences: todo) => {
        if (preferences?.locations && preferences.statuses) {
          this.preferences = preferences;
        } else {
          this.preferences = { locations: {}, statuses: {} };
          this.selectAllPreferencesLocations();
          this.selectAllPreferencesStatuses();
        }
        this.savingPreferences = false;
        // @ts-expect-error
        $('#preferences-modal').modal('toggle');
      });
    };

    this.savePreferences = () => {
      this.savingPreferences = true;
      AdminService.saveQueuePreferences(this.preferences)
        .then(() => this.loadPreferences())
        .then(() => {
          // @ts-expect-error
          $('#preferences-modal').modal('toggle');
          load();
          loadCounts();
        })
        .catch(() => {
          this.savingPreferences = false;
        });
    };

    this.loadPreferences = () =>
      AdminService.getQueuePreferences().then((preferences: todo) => {
        if (preferences.locations && preferences.statuses) {
          // set the search
          this.preferencesSet = true;
          this.search.queueLocations = [] as todo;
          _.each(this.queueLocations, location => {
            this.search.queueLocations[location] =
              preferences.locations[location];
          });
          this.search.queueStatuses = preferences.statuses;
          this.search.locationIndices = getSelectedLocationIndices(
            preferences.locations,
          );
          this.search.statuses = getSelectedStatuses(preferences.statuses);
        } else {
          this.preferencesSet = false;

          this.search.queueLocations = [];
          _.each(this.queueLocations, location => {
            this.search.queueLocations[location] = true;
          });
          this.search.locationIndices = this.deletedLocations
            .reduce((c, isDeleted, index) => {
              if (!isDeleted) c.push(index);
              return c;
            }, [])
            .join();
          this.search.statuses = [
            'new',
            'attempted',
            'confirm',
            'deny',
            'cancel',
            'time_change',
            'time_passed',
          ].join();

          this.search.queueStatuses = [];
          _.each(this.displayStatuses, status => {
            this.search.queueStatuses[status.status] = true;
          });
        }
      });

    this.clearPreferences = () =>
      AdminService.deleteQueuePreferences()
        .then(() => this.loadPreferences())
        .then(() => {
          load();
          loadCounts();
        });

    this.handleSort = (tab: string, sortOptions) => {
      this.$rootScope.$evalAsync(() => {
        this.sortStates[tab] = sortOptions;
        this.load();
      });
    };

    this.getStatusText = (
      appt: EnhancedAppointment & {
        pendingDelivery: {
          deliverCoop: boolean;
          deliverConcierge: boolean;
          isAttempted: boolean;
        };
      },
    ) => {
      if (
        appt.legacyStatus.note ===
        'CONFIRMED: CALL CONCIERGE: MUST DELIVER TO CONCIERGE: Auto Confirmed'
      ) {
        return 'AUTO-CONF: CALL AND DELIVER TO CONCIERGE';
      }

      const { status, isPending, pendingDelivery, type } = appt;

      if (status === 'confirm' && isPending && pendingDelivery.isAttempted) {
        const adminNote = appt.legacyStatus.note.split(':')[2];
        if (
          appt.legacyStatus.note
            .toUpperCase()
            .includes('CONFIRMED: CALL CONCIERGE:')
        ) {
          return `ATTEMPTED: CALL AND DELIVER TO CONCIERGE ${adminNote}`;
        }
        if (
          appt.legacyStatus.note
            .toUpperCase()
            .includes('SEND CONF: CALL HOME INSPECTOR')
        ) {
          return `ATTEMPTED: CALL AND DELIVER TO HOME INSPECTOR ${adminNote}`;
        }
        if (
          appt.legacyStatus.note
            .toUpperCase()
            .includes('SEND CONF: CALL APPRAISER')
        ) {
          return `ATTEMPTED: CALL AND DELIVER TO APPRAISER ${adminNote}`;
        }
        if (
          appt.legacyStatus.note
            .toUpperCase()
            .includes('SEND CONF: CALL PHOTOGRAPHER')
        ) {
          return `ATTEMPTED: CALL AND DELIVER TO PHOTOGRAPHER ${adminNote}`;
        }
        if (
          appt.legacyStatus.note
            .toUpperCase()
            .includes('SEND CONF: CALL PEST CONTROL')
        ) {
          return `ATTEMPTED: CALL AND DELIVER TO PEST CONTROL ${adminNote}`;
        }
      }

      if (
        this.featureEnabled('nonAgentNotificationFeature') &&
        (appt.coop.nonAgentId || appt.coop.customAgentId)
      ) {
        const {
          coop: { approvedNonAgent },
        } = appt;
        const nonAgentStatusMap = {
          deny: 'DENIED',
          cancel: 'CANCEL',
          confirm: 'CONFIRM',
        };
        const naTypeMap: Record<string, string> = {
          'Pest Control': 'PEST CONTROL',
          Photographer: 'PHOTOGRAPHER',
        };
        let naName = naTypeMap[type] || 'HOME INSPECTOR/APPRAISER';
        if (!appt.coop.isNotAgent) {
          naName = 'CUSTOM AGENT';
        }
        const approvedText = `(${naName} NOTIFIED)`;
        const notApprovedText = `(${naName} NOT NOTIFIED)`;

        if (Object.keys(nonAgentStatusMap).includes(appt.status)) {
          // @ts-ignore
          return `${nonAgentStatusMap[appt.status]} ${
            approvedNonAgent ? approvedText : notApprovedText
          }`;
        }
        if (appt.status === 'new') {
          return `NEW SHOWING: ${
            appt.history[appt.history.length - 1]?.actionTaken
          }`;
        }
      }

      return appt.legacyStatus.note;
    };

    // #endregion Public Methods

    // #region Private Methods

    // Hide unassigned tab if it's empty and we're not currently on it
    const updateUnassignedVisibility = () => {
      this.showUnassigned =
        this.counts.unassigned > 0 || this.search.requestType === 'unassigned';
    };

    const loadCounts = () => {
      AdminService.loadResource({
        requestType: 'count',
        locationIndices: this.search.locationIndices,
        statuses: this.search.statuses,
      }).then((result: todo) => {
        this.counts = result;
        updateUnassignedVisibility();
      });
    };

    const load = () => {
      if ($('#searchBox')) $('#searchBox').focus();

      this.dataLoaded = false;
      this.data = [];

      if (!this.search.page) this.search.page = 1;

      const currentRequestType = this.search.requestType;

      if (currentRequestType === 'exclusives') {
        this.dataLoaded = true;
        return;
      }

      const search = this.sortStates[currentRequestType]
        ? { ...this.search, ...this.sortStates[currentRequestType] }
        : this.search;

      return AdminService.loadResource(search).then(
        (result: { data: todo[]; count: number }) => {
          if (currentRequestType !== search.requestType) return;
          if (
            currentRequestType === 'duplicates' &&
            this.showDuplicateHistory
          ) {
            // Massage data for display
            result.data.forEach((d: todo, index) => {
              const newListingData = { ...d };

              // Unique Reps only (by name)
              let allReps = [
                ...newListingData.to.reps,
                ...newListingData.from.reps,
              ];
              allReps = allReps.map(
                ({ firstName, lastName }) => `${firstName} ${lastName}`,
              );
              newListingData.uniqueReps = [...new Set(allReps)];

              // Remove :multi-board from mls numbers
              const accessors = ['to', 'from'];
              accessors.forEach(acc => {
                newListingData[acc].mls_numbers = (
                  newListingData[acc].mls_numbers as todo[]
                )?.map(num => {
                  const newNumber = { ...num };
                  if (newNumber.number.includes(':')) {
                    [newNumber.number] = newNumber.number.split(':');
                  }
                  return newNumber;
                });
              });
              result.data[index] = newListingData;
            });
          }
          this.data = result.data;
          this.count = result.count;
          this.counts[
            search.requestType as keyof AdminDashboardCtrl['counts']
          ] = this.count;
          this.pagination.total = this.datacount || this.count;
          // Handle queue count here; this is incongruous with the other tabs
          // as the Appointments tab is loaded differently from the others
          if (search.requestType === 'queue') {
            this.counts.queue = this.getFilteredAppointmentsCount();
          }

          this.refreshStatuses();
          this.dataLoaded = true;
        },
      );
    };
    this.load = load;

    this.setOfferListing = listing => {
      this.offerListing = listing;
    };

    this.onQueueSearchChange = () => {
      this.counts.queue = this.getFilteredAppointmentsCount();
    };

    // Group filtering methods
    const coopEquals = (a: todo, b: todo) => {
      if (!a) return true;
      if (!a.appointment.coop?.org?.name || !b.appointment.coop?.org?.name) {
        return false;
      }
      return a.appointment.coop.org.name === b.appointment.coop.org.name;
    };
    const addressEquals = (a: todo, b: todo) => {
      if (!a) return true;

      return a.listing.id === b.listing.id;
    };

    const removeNoMoreShowingEntry = (d: todo) => {
      _.remove(this.data, pred => pred.listing.id === d.listing.id);
    };

    const getSelectedLocationIndices = (locationPreferences: todo) => this.locationNames
        .reduce((res, __, index) => {
          if (locationPreferences[index]) res.push(index);
          return res;
        }, [])
        .join();

    const getSelectedStatuses = (statusPreferences: todo) =>
      Object.keys(statusPreferences)
        .filter(key => statusPreferences[key])
        .join();

    // #endregion Private Methods

    // #region Members

    // Lists of requestTypes/tabs that have access to each filter option
    this.branchFilterableTabs = [
      'queue',
      'offers',
      'noMoreShowings',
      'unconfigured',
      'exclusives',
      'unassigned',
      'duplicates',
    ];

    this.statusFilterableTabs = ['queue'];

    // Set of requestTypes/tabs that have access to any filter option
    this.filterableTabs = new Set([
      ...this.branchFilterableTabs,
      ...this.statusFilterableTabs,
    ]);

    // Pagination config
    this.pagination = { maxSize: 5, itemsPerPage: 20 };

    this.MAX_SERVICE_APPTS = 6;

    this.APPOINTMENT_STATUS_FOR_COVID_19_LABEL =
      APPOINTMENT_STATUS_FOR_COVID_19_LABEL;

    this.vm = {};
    // Selected appointments for servicing
    this.selectedAppointments = {};
    // Describes a filter group for similar listings (by address or agent)
    this.group = {};
    // Counts of listings under each tab
    this.counts = {};

    this.appointmentsViewed = {};

    this.showUncombined = false;

    this.displayStatuses = [
      { label: 'New Showing', status: 'new' },
      { label: 'Attempted', status: 'attempted' },
      { label: 'Send Conf', status: 'confirm' },
      { label: 'Send Den', status: 'deny' },
      { label: 'Send Canc', status: 'cancel' },
      { label: 'Time Change', status: 'time_change' },
      { label: 'Time Passed', status: 'time_passed' },
    ];

    // Options for location and status filtering
    this.statuses = ['All', 'Confirm', 'Cancel', 'Deny'];
    this.locationNames = [];
    this.queueLocations = [];
    this.deletedLocations = [];

    // Filter for queue preferences to not show deleted locations.
    // Deleted locations are ignored in the backend, so
    // just hiding it from the user is sufficient.
    this.locationIsNotDeleted = (queueLocationValue: string) =>
      !this.deletedLocations[queueLocationValue as todo];

    this.isAuthorizedToAccessLocation = location =>
      this.auth.roles[this.auth.role] >= this.auth.roles.GLOBAL_ADMIN ||
      this.auth.roles[this.auth.role] === this.auth.roles.TEAM_ADMIN ||
      [this.auth.location, ...(this.auth?.locations || [])].includes(
        location.toString(),
      );

    AdminService.getOrgLocations().then(data => {
      (data.locations as todo[]).forEach(({ name, isDeleted }, index) => {
        if (this.isAuthorizedToAccessLocation(index)) {
          this.queueLocations.push(index);
          this.locationNames[index] = name;
          this.deletedLocations[index] = !!isDeleted;
        }
      });
      this.loadPreferences().then(() => {
        load();
        loadCounts();
      });
    });

    this.search = {
      requestType: this.$routeParams.requestType || 'queue',
      location: this.locationNames[0],
      status: this.statuses[0],
    };

    this.sortStates = {
      offers: { sortBy: 'date-registered', sortDirection: 'asc' },
    };

    this.sort = { type: 'appointment.time', reverse: false };

    this.showDuplicateHistory = false;

    this.auth = this.$rootScope.auth;
    this.openContactDrawer = this.$rootScope.openContactDrawer;

    $rootScope.$watch('labelOverrideMap', () => {
      this.getLabel = field =>
        this.$rootScope.labelOverrideMap[parseLabelFromString(field)]?.label ||
        field;
    });

    $rootScope.$watch('getBoardName', () => {
      this.getBoardName = board =>
        (this.$rootScope.getBoardName && this.$rootScope.getBoardName(board)) ||
        board;
    });

    this.viewDashAppointment = this.$rootScope.viewDashAppointment;
    this.offerListing = null;

    // Update counts every 15 minutes
    const countPromise = this.$interval(loadCounts, 15 * 60 * 1000);
    // Refresh statuses every minute
    const refreshStatusesPromise = this.$interval(
      this.refreshStatuses,
      1000 * 60,
    );
    this.refreshStatuses();
    // #endregion Members

    // #feature flags

    this.featureEnabled = featureEnabled;

    // #endregion feature flags
  }
}

angular.module('bb.admin').component('bbAdminDashboard', {
  controller: AdminDashboardCtrl,
  template: pageTemplate,
});

ngAnnotate(AdminDashboardCtrl);
